import React, { useState, useRef, useEffect } from "react";
import {
  useLocation,
  useOutletContext,
  Outlet,
  useNavigate,
  useParams,
} from "react-router-dom";
import ApiMap from "../../utils/ApiMap";
import AuthService from "../../utils/AuthService";
import axios from "axios";
import {
  Box,
  Typography,
  LinearProgress,
  IconButton,
  CircularProgress,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { Menu as MenuIcon } from "@mui/icons-material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import GameStartLink from "../services/GameStartLink";

const LessonPage = () => {
  console.log("lesson Page");
  const location = useLocation();
  const { lessonName, lessons } = location.state || {};
  const chapterinfo = React.useRef(null);
  const [progress, setProgress] = useState(0); // Overall progress
  const [chapter, setChapter] = useState(null); // Store the fetched lesson content
  const [isLoading, setIsLoading] = useState(true); // Loading state
  const [error, setError] = useState(null); // Error state
  const [isContentLoaded, setIsContentLoaded] = useState(false);
  const [isLessonLoading, setIsLessonLoading] = useState(false);
  const [loadingNxtPg, setLoadingNxtPg] = React.useState(false);
  //const [loadingPrevPg, setLoadingPrevPg] = React.useState(false);
  const { courseId, moduleId, chapterId, lessonId } = useParams();
  const [loadingSubmissions, setLoadingSubmissions] = useState(true);
  const [scrollProgress, setScrollProgress] = useState({});
  const lessonRefs = useRef([]);
  const contentId = lessonId;
  const {
    setIsSidebarHidden,
    lessonProgress,
    sidebarWidth,
    quizSubmissions,
    handleProgressClick,
    setManualSidebarToggle,
  } = useOutletContext();
  const navigate = useNavigate();
  const isQuizPage = location.pathname.includes("quizpage");
  const isAssignmentPage = location.pathname.includes("assignment");
  const [userScores, setUserScores] = useState(null);
  const lessonQuizSubmissions = quizSubmissions?.[moduleId]?.[lessonId] || [];

  const handleToggleSidebar = () => {
    setManualSidebarToggle(true); // ✅ mark as manually toggled
    setIsSidebarHidden((prev) => !prev);
  };

  const currentLessonData = lessons?.find(
    (l) => String(l.id) === String(lessonId)
  );
  const isStudent = AuthService.isStudent(courseId);
  const finalOpenForStudent =
    currentLessonData?.openForStudent ?? chapter?.OpenForStudent;
  const isLocked = finalOpenForStudent === false;

  useEffect(() => {
    //if (isQuizPage) return;
    setIsLessonLoading(true);
    const fetchData = async () => {
      try {
        const resp = await axios(
          `${ApiMap.CONTENT}/coursecontent/${courseId}/module/${moduleId}/chapter/${chapterId}/lesson/${contentId}`,
          AuthService.getAuthHeader()
        );
        chapterinfo.current = resp.data;
        console.log(resp.data);
        setChapter({
          Id: resp.data.Id,
          Name: resp.data.Name,
          ModuleName: resp.data.ModuleName,
          Title: resp.data.Title,
          Value: resp.data.Value,
          ContentFormat: resp.data.ContentFormat,
          Time: resp.data.DurationMin,
          totalPages: resp.data.TotalPages,
          currPage: resp.data.CurPgNum,
          ContentType: resp.data.ContentType,
          dateCompleted: resp.data.DateCompleted,
          OpenForStudent: resp.data.OpenForStudent, // ⭐ Add this
        });

        setIsContentLoaded(true);
      } catch (err) {
        setError("Failed to load lesson content. Please try again.");
      } finally {
        setIsLoading(false);
        setIsLessonLoading(false);
      }
    };

    if (AuthService.isLoggedIn() && contentId) {
      fetchData();
    }
  }, [contentId, courseId, moduleId, chapterId]); // Runs when contentId changes

  const getChapter = async (pgNum) => {
    const resp = await axios(
      `${ApiMap.CONTENT}/coursecontent/${courseId}/module/${moduleId}/chapter/${chapterId}/lesson/${contentId}?pgnum=${pgNum}`,
      AuthService.getAuthHeader()
    );

    chapterinfo.current.CurPgNum = resp.data.CurPgNum;
    setChapter({
      Name: resp.data.Name,
      Title: resp.data.Title,
      Value: resp.data.Value,
      ContentFormat: resp.data.ContentFormat,
      ModuleName: resp.data.ModuleName,
      Time: resp.data.DurationMin,
      totalPages: resp.data.TotalPages,
      currPage: resp.data.CurPgNum,
      ContentType: resp.data.ContentType,
    });
  };

  const completionProgress =
    lessonProgress?.[moduleId]?.[lessonId] ??
    (chapter?.dateCompleted ? 100 : 0);

  const isCompleted = completionProgress === 100;

  useEffect(() => {
    if (lessonName && lessonRefs.current.length > 0 && chapter) {
      const targetIndex = chapter.Name === lessonName ? 0 : -1;
      if (targetIndex >= 0 && lessonRefs.current[targetIndex]) {
        lessonRefs.current[targetIndex].scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }
  }, [lessonName, chapter]);

  useEffect(() => {
    const handleScroll = () => {
      if (!lessonRefs.current[0]) return;

      const updatedScrollProgress = { ...scrollProgress };
      const lessonRef = lessonRefs.current[0];
      const rect = lessonRef.getBoundingClientRect();
      const lessonHeight = lessonRef.offsetHeight;

      const scrollTop = Math.max(0, window.innerHeight - rect.top);
      const lessonScrollValue = Math.min((scrollTop / lessonHeight) * 100, 100);

      // ✅ Store only in scrollProgress, not lessonProgress
      updatedScrollProgress[contentId] = Math.round(lessonScrollValue);

      // overall progress
      const visiblePercentage = Math.min(
        Math.max(0, window.innerHeight - rect.top) / lessonHeight,
        1
      );
      const totalProgress = visiblePercentage * 100;

      setScrollProgress(updatedScrollProgress); // ✅ separate from lessonProgress
      setProgress(totalProgress);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [contentId, isContentLoaded, scrollProgress]);

  useEffect(() => {
    if (!lessonQuizSubmissions || lessonQuizSubmissions.length === 0) return;
    const fetchScores = async () => {
      try {
        const resp = await axios(
          `${ApiMap.COURSE_LEADERBOARD}/course/${courseId}/studentscores`,
          AuthService.getAuthHeader()
        );
        console.log(resp);
        setUserScores(resp.data);
      } catch (error) {
        console.error("Failed to fetch leaderboard:", error);
      } finally {
        setLoadingSubmissions(false); // ✅ stop loading
      }
    };

    fetchScores();
  }, [quizSubmissions, lessonId]);

  const handleNext = () => {
    // Check if current page is less than total pages
    if (chapterinfo.current.CurPgNum < chapterinfo.current.TotalPages) {
      setLoadingNxtPg(true); // Set loading to true to show the spinner
      getChapter(parseInt(chapterinfo.current.CurPgNum) + 1)
        .then(() => {
          setLoadingNxtPg(false); // Set loading to false once the chapter is fetched
        })
        .catch(() => {
          setLoadingNxtPg(false); // In case of error, set loading to false
        });
    }
  };

  // Map of submissions for quick lookup
  const submissionsMap = new Map(
    lessonQuizSubmissions.map((sub) => [sub.userId, sub])
  );

  // Final list: every student + their quiz status
  const studentStatuses =
    userScores?.map((student) => {
      const submission = submissionsMap.get(student.userId);
      return {
        name: `${student.fname} ${student.lname}`,
        dateSubmitted: submission?.dateSubmitted || null,
        gradeObtained: submission?.gradeObtained ?? null,
        status: submission ? "Submitted" : "Not Attempted",
      };
    }) || [];

  const sortedStudents = [...studentStatuses].sort((a, b) => {
    if (a.status === b.status) return 0;
    return a.status === "Submitted" ? -1 : 1;
  });

  const handleBack = () => {
    getChapter(chapterinfo.current.CurPgNum - 1);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: "center", mt: 5 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }
  const handleGoToQuiz = () => {
    navigate("quizpage", {
      state: {
        // courseId,
        // moduleId,
        // chapterId,
        // contentId,
        chapterName: chapter.Name,
      },
    });
  };

  const handleAssignment = () => {
    navigate("assignment", {
      state: {
        ...location.state, // keep all the original state (lessons, etc.)
      },
    });
  };

  if (!chapter) {
    return (
      <Box sx={{ textAlign: "center", mt: 5 }}>
        <Typography>No Lesson available</Typography>
      </Box>
    );
  }

  if (isStudent && isLocked) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography
          variant="h4"
          color="error"
          fontWeight={700}
          textAlign="center"
          sx={{ mb: 2 }}
        >
          Lesson Locked
        </Typography>

        <Typography
          variant="body1"
          textAlign="center"
          sx={{ color: "gray", mb: 3 }}
        >
          This lesson is not available for students yet.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          position: "relative",
          padding: "16px",
        }}
      >
        <IconButton
          onClick={handleToggleSidebar}
          sx={{ position: "fixed", top: 0, left: sidebarWidth, zIndex: 1000 }}
        >
          <MenuIcon />
        </IconButton>
      </Box>
      {/* Progress Bar */}
      <Box sx={{ padding: "20px" }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 700,
            textAlign: "center",
            marginBottom: "20px",
            color: "primary.main",
          }}
        >
          {chapter.ModuleName || "Course Name"}
        </Typography>
      </Box>
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: sidebarWidth, // to align with your layout
          right: 0,
          zIndex: 1200,
        }}
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              flex: 1, // ensures the progress bar takes remaining space
            }}
          />
        </Box>
      </Box>
      {/* Lesson Content */}
      {!isQuizPage &&
        !isAssignmentPage &&
        chapter &&
        (isLessonLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <LinearProgress
              color="secondary"
              sx={{
                width: "200px", // Controls the progress bar width
                height: "4px",
              }}
            />
          </Box>
        ) : (
          <Box
            sx={{
              padding: "25px",
              minWidth: "400px",
              //maxWidth: "920px",
              margin: "0 auto",
            }}
          >
            <Box
              ref={(el) => (lessonRefs.current[0] = el)}
              sx={{
                padding: 2,
                borderRadius: 2,
                boxShadow: 1,
                marginBottom: 5,
              }}
            >
              <Box
                sx={{
                  display: "flex", // ✅ correct
                  justifyContent: "space-between", // ✅ spread left & right
                  alignItems: "center",
                }}
              >
                {/* Left side (Lesson info) */}
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 500,
                    fontStyle: "italic",
                    color: "secondary.main",
                    padding: "2px 5px",
                  }}
                >
                  Lesson # {chapter.Id} <br />
                  Duration: {chapter.Time} minutes
                </Typography>

                {AuthService.isFaculty(courseId) && (
                  <Button
                    variant="contained"
                    size="small" // ✅ smaller button
                    color={isCompleted ? "primary" : "secondary"}
                    onClick={() =>
                      handleProgressClick(
                        lessonId,
                        moduleId,
                        chapter.Id,
                        chapter?.dateCompleted
                      )
                    }
                    sx={{
                      zIndex: 1000,
                      display: "flex",
                      alignItems: "center",
                      gap: 1, // space between icon & text
                      fontSize: "0.8rem", // ✅ smaller text
                      padding: "4px 8px", // ✅ compact button
                    }}
                  >
                    {isCompleted ? (
                      <>
                        <CheckCircleIcon fontSize="small" />
                        Completed
                      </>
                    ) : (
                      <>
                        <RadioButtonUncheckedIcon fontSize="small" />
                        Mark as Complete
                      </>
                    )}
                  </Button>
                )}
              </Box>
              <Box
                sx={{
                  lineHeight: 1.5,
                }}
              >
                {chapter.Value !== "No Content yet" ? (
                  <Box
                    dangerouslySetInnerHTML={{ __html: chapter?.Value }}
                    className="lesson-content"
                    sx={{
                      width: "100%",
                      fontSize: "0.95rem",
                      lineHeight: 1.7,
                      wordBreak: "break-word",
                      whiteSpace: "normal",
                      maxWidth: "100%",
                      overflow: "visible",
                      "& h1:first-of-type": {
                        textAlign: "center",
                      },
                      "& img": {
                        maxWidth: "100%",
                        height: "auto",
                        display: "block",
                        borderRadius: 1,
                        margin: "16px auto",
                      },
                      "& table": {
                        borderCollapse: "collapse",
                        width: "100%",
                        margin: "16px 0",
                      },
                      "& th, & td": {
                        border: "1px solid #d0d7de",
                        padding: "8px 12px",
                        wordBreak: "break-word",
                        textAlign: "left",
                      },
                      "& th": {
                        backgroundColor: "#f6f8fa",
                        fontWeight: 600,
                      },
                      "& iframe, & embed, & video": {
                        width: "100%",
                        maxWidth: "100%",
                        border: 0,
                        borderRadius: "8px",
                        margin: "16px auto",
                        display: "block",
                      },
                      "& blockquote": {
                        borderLeft: "4px solid #d0d7de",
                        margin: "16px 0",
                        paddingLeft: "16px",
                        color: "text.secondary",
                        backgroundColor: "#f9fafb",
                      },
                    }}
                  />
                ) : (
                  <>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 600,
                        textAlign: "center",
                        marginBottom: "10px",
                      }}
                    >
                      {chapter.Name}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 400,
                        textAlign: "center",
                        marginBottom: "30px",
                        color: "text.secondary",
                      }}
                    >
                      {chapter.Title.replace(/<[^>]*>/g, "")}
                    </Typography>
                  </>
                )}

                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  {chapter.ContentType !== "formative_assessment" &&
                    chapter.ContentType !== "assignment" &&
                    chapter.ContentType !== "writing" && (
                      <Typography variant="body2" sx={{ marginTop: "8px" }}>
                        Pages:
                        {` ${chapterinfo.current.CurPgNum} / ${chapter.totalPages}`}
                      </Typography>
                    )}
                </Box>

                {chapter.totalPages > 1 &&
                chapter.ContentType !== "assignment" &&
                chapter.ContentType !== "writing" ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginTop: 3,
                    }}
                  >
                    <Button
                      variant="contained"
                      onClick={handleBack}
                      disabled={chapterinfo.current.CurPgNum === 1}
                    >
                      ← Back
                    </Button>

                    <Button
                      variant="contained"
                      disabled={
                        chapterinfo.current.CurPgNum >=
                          chapterinfo.current.TotalPages || loadingNxtPg
                      }
                      onClick={handleNext}
                      startIcon={
                        loadingNxtPg ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : null
                      }
                    >
                      {loadingNxtPg ? "Loading..." : "Next →"}
                    </Button>
                  </Box>
                ) : null}
              </Box>
              {chapter.ContentType === "formative_assessment" && (
                <Box
                  sx={{
                    textAlign: "right",
                    marginTop: 2,
                    display: "flex",
                    gap: 2,
                    justifyContent: "flex-end",
                  }}
                >
                  {AuthService.isFaculty(courseId) ? (
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{
                        padding: "5px 10px",
                        borderRadius: "20px",
                        fontWeight: "bold",
                      }}
                      onClick={() =>
                        navigate(
                          `/OpenGameroom/course/${courseId}/module/${moduleId}/chapter/${chapterId}/game/${contentId}`
                        )
                      }
                    >
                      Start Game
                      <ArrowForwardIcon sx={{ marginLeft: "8px" }} />
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant="contained"
                        color="primary"
                        sx={{
                          padding: "5px 10px",
                          borderRadius: "20px",
                          fontWeight: "bold",
                        }}
                        onClick={handleGoToQuiz}
                      >
                        Quiz
                        <ArrowForwardIcon
                          sx={{
                            marginLeft: "8px",
                          }}
                        />
                      </Button>
                    </>
                  )}
                </Box>
              )}

              {chapter.ContentType === "formative_assessment" &&
                AuthService.isFaculty(courseId) && (
                  <Box sx={{ my: 2 }}>
                    {loadingSubmissions ? (
                      <Typography
                        variant="body1"
                        sx={{
                          textAlign: "center",
                          color: "text.secondary",
                          mt: 2,
                        }}
                      >
                        Loading submissions...
                      </Typography>
                    ) : studentStatuses.length > 0 ? (
                      <TableContainer component={Paper}>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>
                                <b>#</b>
                              </TableCell>
                              <TableCell>
                                <b>Student Name</b>
                              </TableCell>
                              <TableCell>
                                <b>Date Submitted</b>
                              </TableCell>
                              <TableCell>
                                <b>Grades Obtained</b>
                              </TableCell>
                              <TableCell>
                                <b>Status</b>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {sortedStudents.map((student, idx) => (
                              <TableRow key={idx}>
                                <TableCell>{idx + 1}</TableCell>
                                <TableCell>{student.name}</TableCell>
                                <TableCell>
                                  {student.dateSubmitted
                                    ? new Date(
                                        student.dateSubmitted
                                      ).toLocaleString()
                                    : "—"}
                                </TableCell>
                                <TableCell>
                                  {student.gradeObtained ?? "—"}
                                </TableCell>
                                <TableCell>{student.status}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography
                        variant="body1"
                        sx={{
                          textAlign: "center",
                          mt: 2,
                          color: "text.secondary",
                          fontStyle: "italic",
                        }}
                      >
                        No submissions yet
                      </Typography>
                    )}
                  </Box>
                )}
              {(chapter.ContentType === "assignment" ||
                chapter.ContentType === "writing") && (
                <Box sx={{ textAlign: "right", marginTop: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{
                      padding: "5px 10px",
                      borderRadius: "20px",
                      fontWeight: "bold",
                    }}
                    onClick={handleAssignment}
                  >
                    Enter
                    <ArrowForwardIcon
                      sx={{
                        marginLeft: "8px",
                      }}
                    />
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        ))}
      <Outlet />
    </Box>
  );
};

export default LessonPage;
